import { DOCUMENT, inject } from '@angular/core';
import { ActivatedRouteSnapshot, ViewTransitionInfo } from '@angular/router';
import { TopLayer } from '@app/core/layers/top-layer';

export type FlowDirection = 'forward' | 'back';

/** El paso del onboarding de una ruta (`data.flowStep`), o `null` si está fuera del flujo. */
export function flowStepOf(root: ActivatedRouteSnapshot): number | null {
  let route = root;
  while (route.firstChild) {
    route = route.firstChild;
  }
  const step: unknown = route.data['flowStep'];
  return typeof step === 'number' ? step : null;
}

/** Hacia dónde va el cambio de paso. `null` si no lo hay: mismo paso o una ruta fuera del flujo. */
export function flowDirection(from: ActivatedRouteSnapshot, to: ActivatedRouteSnapshot): FlowDirection | null {
  const fromStep = flowStepOf(from);
  const toStep = flowStepOf(to);
  if (fromStep === null || toStep === null || fromStep === toStep) {
    return null;
  }
  return toStep > fromStep ? 'forward' : 'back';
}

/**
 * Enciende la animación de `flow-transition.scss` durante un cambio de paso. Corre antes de la foto de la página vieja,
 * que el navegador saca en el próximo cuadro, así que los `view-transition-name` que activa ya entran en ella.
 *
 * Sin la marca no se anima nada y la página cambia de golpe:
 * - Fuera del flujo no hay un "siguiente paso" que mostrar.
 * - Un `<dialog>` abierto queda en la foto del fondo y el contenido se pintaría encima de él. Pasa si la sesión se cierra
 *   con un modal abierto; el de finalizar se cierra antes de navegar.
 */
export function animateFlowStep({ transition, from, to }: ViewTransitionInfo): void {
  const direction = flowDirection(from, to);
  if (!direction || inject(TopLayer).hasOpenModal()) {
    return;
  }
  const { dataset } = inject(DOCUMENT).documentElement;
  dataset['flowDirection'] = direction;
  const clear = () => delete dataset['flowDirection'];
  void transition.finished.then(clear, clear);
}
